import type { UnitDisplayMap } from '@dl/shared';

/**
 * Unit display preferences (08-units.md §5, #249).
 *
 * Which unit each dimension RENDERS in is a SETTING, not a definition. There
 * are two tiers (resolved by `resolveDisplayUnit` in `@dl/shared` `units.ts`,
 * in this order):
 *
 *   1. the user's per-dimension override — **User-scope**, NOT campaign config
 *      (§5). Persisted in `localStorage`, keyed per campaign so a user can
 *      prefer miles in one campaign and km in another. Never rides the op log.
 *   2. the campaign default — a `config.set { key: 'unit_display_defaults' }`
 *      value folded from the op log (`config-fold.ts`). Pushed here by
 *      `app.ts:rebuildEntities` so the module-scope render chokepoint
 *      (`formatNumberValueForDisplay`) can read it synchronously.
 *
 * The campaign default lives in a module-scope holder (not Model state) for the
 * same reason `homebrew-defs.ts` does: it's read synchronously inside reactive
 * accessors, and llui's Model must stay JSON-serialisable. A monotonic epoch
 * bumps on every change so the unit-aware render memos invalidate on the next
 * commit. The stored `canonical_value` is NEVER touched — only the rendered
 * unit converts; export carries canonical values, never the display projection.
 */

let _campaignDefaults: UnitDisplayMap = {};
let _campaignId: string | null = null;
let _epoch = 0;

/** localStorage key for a campaign's per-user unit overrides. */
function userOverrideKey(campaignId: string): string {
  return `dl.unitDisplayOverrides.${campaignId}`;
}

/**
 * Replace the campaign default map (from the folded `unit_display_defaults`
 * config value) and note the active campaign id (scopes the user override).
 * Bumps the epoch.
 */
export function setUnitDisplayPrefs(campaignId: string | null, defaults: UnitDisplayMap): void {
  _campaignId = campaignId;
  _campaignDefaults = defaults;
  _epoch += 1;
}

/** The current campaign per-dimension default display-unit map. */
export function campaignUnitDefaults(): UnitDisplayMap {
  return _campaignDefaults;
}

/**
 * The current user's per-dimension override map (User-scope), for the active
 * campaign. Reads `localStorage`; returns `{}` when none is set or storage is
 * unavailable (Safari private-mode / SSR). A malformed payload is treated as
 * empty rather than thrown — a display preference must never break rendering.
 */
export function userUnitOverrides(): UnitDisplayMap {
  if (_campaignId === null || typeof localStorage === 'undefined') return {};
  try {
    const raw = localStorage.getItem(userOverrideKey(_campaignId));
    if (raw === null) return {};
    const parsed: unknown = JSON.parse(raw);
    if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    const out: UnitDisplayMap = {};
    for (const [dim, unit] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof unit === 'string' && unit.length > 0) out[dim] = unit;
    }
    return out;
  } catch {
    return {};
  }
}

/**
 * Set (or, with `unitId === null`, clear) the user's display-unit override for
 * one dimension in the active campaign. Persists to `localStorage`; bumps the
 * epoch so the next render re-resolves. No-op when there's no active campaign
 * or storage is unavailable.
 */
export function setUserUnitOverride(dimensionId: string, unitId: string | null): void {
  if (_campaignId === null || typeof localStorage === 'undefined') return;
  const next = userUnitOverrides();
  if (unitId === null) delete next[dimensionId];
  else next[dimensionId] = unitId;
  try {
    if (Object.keys(next).length === 0) localStorage.removeItem(userOverrideKey(_campaignId));
    else localStorage.setItem(userOverrideKey(_campaignId), JSON.stringify(next));
    _epoch += 1;
  } catch {
    // Persisting a display preference is a nicety; a quota/private-mode
    // failure just means the override doesn't stick. No correctness impact.
  }
}

/** Monotonic counter — bumps on every prefs change. Memo cache key. */
export function unitDisplayEpoch(): number {
  return _epoch;
}

/** Test seam — reset campaign defaults + active campaign to empty. */
export function _resetUnitDisplayPrefs(): void {
  _campaignDefaults = {};
  _campaignId = null;
  _epoch += 1;
}
